import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { icon } from '../lib/icons';
import { t, LocaleController } from '../lib/i18n';
import { urlHref, urlLabel, telHref, mailHref, socialLabel } from '../lib/helpers';
import type { CardExtras, Contact } from '../lib/collected';

/**
 * <card-extras> — everything a card carries BEHIND its front.
 *
 * The front of a card is always the standard contact card: a name, a title, a
 * company, ways to reach someone. That is what makes a tapped card instantly
 * legible, and it must not change however much extra content its owner adds.
 * So this renders BELOW the card and never touches it — no amount of links or
 * promo copy can alter the card's layout, and it cannot overflow the fixed
 * credit-card ratio because it is not inside it.
 *
 * The strings arrive already validated against a closed schema on the server
 * (platform/extras): no markup, no executable URL schemes, no characters that
 * can disguise where a link goes.
 */
@customElement('card-extras')
export class CardExtrasPanel extends LitElement {
  @property({ attribute: false }) extras?: CardExtras;
  // Re-renders this element when the interface language changes.
  readonly i18n = new LocaleController(this);
  /** Additional ways to reach the person. The card FRONT shows only their
   *  primary phone and email — its field grid is fixed — so any others belong
   *  here, where the list can grow without touching the card. */
  @property({ attribute: false }) phones?: Contact[];
  @property({ attribute: false }) emails?: Contact[];
  /** The owner's profiles elsewhere. Rendered here because a card with a
   *  designed back image has no themed back to carry them — without this
   *  slot, socials on such a card appear NOWHERE (the 2026-08-23 gap). */
  @property({ attribute: false }) socials?: Record<string, string> | null;

  static styles = css`
    :host { display:block; }
    .wrap { display:flex; flex-direction:column; gap:16px; }

    .head { font:600 10px/1 var(--font-mono,ui-monospace,monospace); letter-spacing:calc(.16em * var(--track, 1));
      text-transform:uppercase; color:var(--app-faint); }

    /* Links read as a list of destinations, not as buttons competing with the
       card's own actions. */
    .links { display:flex; flex-direction:column; gap:8px; }
    .link { display:flex; align-items:center; gap:11px; padding:12px 14px; border-radius:14px;
      text-decoration:none; color:var(--app-text); background:var(--app-surface,#16181f);
      border:1px solid var(--app-outline,#2a2f3a);
      transition:border-color var(--dur-2,180ms), transform var(--dur-2,180ms); }
    .link:hover { border-color:var(--app-primary,#1b7a52); transform:translateY(-1px); }
    .link .ico { color:var(--app-muted); flex:none; }
    .link .txt { display:flex; flex-direction:column; gap:2px; min-width:0; flex:1; }
    .link .lbl { font-size:14px; font-weight:600; }
    .link .dest { font-size:11.5px; color:var(--app-muted); white-space:nowrap;
      overflow:hidden; text-overflow:ellipsis; }
    .link .go { color:var(--app-faint); flex:none; }

    /* The promo is visually distinct from the links so it reads as an offer
       rather than as another destination. */
    .promo { padding:16px; border-radius:16px; background:color-mix(in srgb,var(--app-primary,#1b7a52) 10%, transparent);
      border:1px solid color-mix(in srgb,var(--app-primary,#1b7a52) 26%, transparent);
      display:flex; flex-direction:column; gap:8px; }
    .promo .hl { font-family:var(--font-brand,system-ui); font-weight:700; font-size:17px;
      letter-spacing:calc(-.01em * var(--track, 1)); color:var(--app-text); }
    /* pre-wrap so the author's line breaks survive; the text itself is plain,
       validated on the server. */
    .promo .body { font-size:13.5px; line-height:1.5; color:var(--app-muted); white-space:pre-wrap; }
    .promo .cta { align-self:flex-start; margin-top:2px; display:inline-flex; align-items:center; gap:7px;
      padding:10px 16px; border-radius:999px; text-decoration:none; font-size:13.5px; font-weight:600;
      color:var(--app-on-primary,#04120b); background:var(--app-primary,#1b7a52); }
  `;

  render() {
    const links = this.extras?.links ?? [];
    const promo = this.extras?.promo;
    const phones = this.phones ?? [];
    const emails = this.emails ?? [];
    const socials = Object.entries(this.socials ?? {}).filter(([k, v]) => k !== 'website' && v);
    if (!links.length && !promo && !phones.length && !emails.length && !socials.length) return nothing;

    return html`
      <div class="wrap">
        ${promo ? this.renderPromo(promo) : nothing}
        ${phones.length || emails.length
          ? html`
            <div>
              <div class="head" style="margin-bottom:9px">${t('ex.also')}</div>
              <div class="links">
                ${phones.map((c) => this.renderContact(c, 'call', telHref(c.value)))}
                ${emails.map((c) => this.renderContact(c, 'mail', mailHref(c.value)))}
              </div>
            </div>`
          : nothing}
        ${links.length
          ? html`
            <div>
              <div class="head" style="margin-bottom:9px">${t('ex.more')}</div>
              <div class="links">
                ${links.map((l) => html`
                  <a class="link" href=${urlHref(l.url)} target="_blank" rel="noopener noreferrer">
                    ${icon('link')}
                    <span class="txt">
                      <span class="lbl">${l.label}</span>
                      <span class="dest">${urlLabel(l.url)}</span>
                    </span>
                    ${icon('arrow_forward', 'go')}
                  </a>`)}
              </div>
            </div>`
          : nothing}
        ${socials.length
          ? html`
            <div>
              <div class="head" style="margin-bottom:9px">${t('ex.social')}</div>
              <div class="links">
                ${socials.map(([k, v]) => html`
                  <a class="link" href=${urlHref(v)} target="_blank" rel="noopener noreferrer">
                    ${icon('social')}
                    <span class="txt">
                      <span class="lbl">${socialLabel(k)}</span>
                      <span class="dest">${urlLabel(v)}</span>
                    </span>
                    ${icon('arrow_forward', 'go')}
                  </a>`)}
              </div>
            </div>`
          : nothing}
      </div>`;
  }

  /** One extra contact, tappable like every other row on the card. */
  private renderContact(c: Contact, ic: 'call' | 'mail', href: string) {
    return html`
      <a class="link" href=${href}>
        ${icon(ic)}
        <span class="txt">
          <span class="lbl">${c.value}</span>
          ${c.label ? html`<span class="dest">${c.label}</span>` : nothing}
        </span>
      </a>`;
  }

  private renderPromo(p: NonNullable<CardExtras['promo']>) {
    return html`
      <div class="promo">
        ${p.headline ? html`<div class="hl">${p.headline}</div>` : nothing}
        ${p.body ? html`<div class="body">${p.body}</div>` : nothing}
        ${p.cta && p.url
          ? html`<a class="cta" href=${urlHref(p.url)} target="_blank" rel="noopener noreferrer">
              ${p.cta}${icon('arrow_forward')}
            </a>`
          : nothing}
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'card-extras': CardExtrasPanel; }
}
